import LinkIcon from "@mui/icons-material/Link";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { GoogleLogin } from "@react-oauth/google";
import * as jwt from "jsonwebtoken";
import * as React from "react";
import { Link, Navigate } from "react-router-dom";
import { Identity, useIdentity } from "../../containers/IdentityContainer";
import { getRedirectUrl } from "../../utils/redirectManager";
import { createUnauthenticatedPostgrest } from "../../utils/unauthenticatedApi";
// import logo from "../../../airplx-logo-transparent-square-no-text.png";
const logo = "/airplx-logo-transparent-square-no-text.png";

export const MagicLinkLoginButton: React.FC<{}> = () => {
  return (
    <Tooltip
      title={`Magic Link Login allows user to securely login without a password. A link will be sent to the email address associated with your account.`}
    >
      <Button
        component={Link}
        to="/login/link"
        variant="outlined"
        fullWidth
        startIcon={<LinkIcon />}
        sx={{
          borderRadius: 1,
          borderColor: "divider",
          "&:hover": {
            borderColor: "text.primary",
            backgroundColor: "action.hover",
          },
        }}
      >
        Login with Magic Link
      </Button>
    </Tooltip>
  );
};

type LoginStep = "email" | "password" | "magic-link" | "setup-password";

export const Login: React.FC<{
  magicLink?: boolean;
}> = () => {
  const {
    isLoggedIn,
    loginWithMicrosoft,
    loginWithGoogle,
    loginWithPassword,
    hasPassword,
  } = useIdentity();
  // Use unauthenticated client for whitelist check (user not logged in yet)
  const postgrest = React.useMemo(() => createUnauthenticatedPostgrest(), []);

  const [email, setEmail] = React.useState<string>("");
  const [password, setPassword] = React.useState<string>("");
  const [step, setStep] = React.useState<LoginStep>("email");
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>("");
  const [showEmailLogin, setShowEmailLogin] = React.useState<boolean>(false);

  if (isLoggedIn) {
    return <Navigate to="/" replace />;
  }

  const handleEmailSubmit = async () => {
    if (!email) {
      setError("Please enter your email address");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // First check if whitelisted for password auth
      const isWhitelisted = await postgrest.rpc(
        "is_password_auth_whitelisted",
        {
          p_email: email,
        }
      );

      if (!isWhitelisted.data) {
        // Not whitelisted - show helpful error
        setError(
          "Password authentication is not available for your account. Please use Microsoft or Google to sign in."
        );
        setLoading(false);
        return;
      }

      // Whitelisted - check if they have a password set
      const emailHasPassword = await hasPassword(email);

      if (emailHasPassword) {
        setStep("password");
      } else {
        // Whitelisted but no password set - prompt to set up password
        setStep("setup-password");
      }
    } catch (err) {
      setError("An error occurred. Please try again.");
      console.error("Error checking email:", err);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordLogin = async () => {
    if (!password) {
      setError("Please enter your password");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const success = await loginWithPassword(email, password);

      if (success) {
        // Redirect to stored URL or default to home page
        // Use window.location to force full page reload so identity re-initializes
        const redirectUrl = getRedirectUrl("/");
        window.location.href = `/#${redirectUrl}`;
      } else {
        setError("Invalid email or password");
      }
    } catch (err) {
      setError("An error occurred. Please try again.");
      console.error("Error logging in:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setStep("email");
    setPassword("");
    setError("");
    setShowEmailLogin(false);
  };

  return (
    <>
      <Stack
        direction="column"
        justifyContent="center"
        sx={{ height: "100%", width: "100%" }}
      >
        <Box
          sx={{
            width: 368,
            margin: "auto",
            border: 2,
            borderColor: "divider",
            p: 3,
            mt: 20,
          }}
        >
          <Stack
            direction="column"
            alignItems="center"
            justifyContent="center"
            spacing={1}
          >
            <Typography align="center" variant="h4">
              Welcome Back
            </Typography>
            <Typography
              align="center"
              variant="subtitle1"
              color="text.secondary"
            >
              {step === "email" && "Sign in to your account"}
              {step === "password" && "Enter your password"}
              {step === "magic-link" && "We'll send you a login link"}
              {step === "setup-password" && "Set up your password"}
            </Typography>
          </Stack>
          <br />
          <br />

          {/* Primary: SSO Options */}
          <Stack direction="column" spacing={2}>
            {step === "email" && (
              <>
                <Button
                  variant="outlined"
                  fullWidth
                  sx={{ borderRadius: 1, borderColor: "divider" }}
                  onClick={async () => {
                    await loginWithMicrosoft();
                    // Redirect to stored URL or default to home page
                    // Use window.location to force full page reload so identity re-initializes
                    const redirectUrl = getRedirectUrl("/");
                    window.location.href = `/#${redirectUrl}`;
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                    sx={{ width: "100%" }}
                  >
                    <img
                      src="https://img.icons8.com/color/48/000000/microsoft.png"
                      style={{ width: 25 }}
                    />
                    <Typography variant="body2" sx={{ color: "text.primary" }}>
                      Login with Microsoft
                    </Typography>
                    <span></span>
                  </Stack>
                </Button>

                <GoogleLogin
                  width="320px"
                  onSuccess={async (credentialResponse) => {
                    const token = credentialResponse.credential;
                    const identity = jwt.decode(token);
                    await loginWithGoogle(token, identity as Identity);
                    // Redirect to stored URL or default to home page
                    // Use window.location to force full page reload so identity re-initializes
                    const redirectUrl = getRedirectUrl("/");
                    window.location.href = `/#${redirectUrl}`;
                  }}
                  onError={() => {
                    console.log("Login Failed");
                  }}
                />

                <Typography
                  variant="body2"
                  align="center"
                  sx={{
                    mt: 2,
                    cursor: "pointer",
                    color: "text.secondary",
                    "&:hover": { textDecoration: "underline" },
                  }}
                  onClick={() => setShowEmailLogin(true)}
                >
                  Use password instead (for special cases only)
                </Typography>

                {showEmailLogin && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mb: 1, display: "block" }}
                    >
                      Password authentication is restricted per your
                      organization's policy. Most users should use Microsoft or
                      Google login above.
                    </Typography>
                    <TextField
                      label="Email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onKeyPress={(e) =>
                        e.key === "Enter" && handleEmailSubmit()
                      }
                      fullWidth
                      error={Boolean(error)}
                      helperText={error}
                      disabled={loading}
                      size="small"
                    />
                    <Button
                      variant="outlined"
                      onClick={handleEmailSubmit}
                      fullWidth
                      disabled={loading || !email}
                      size="small"
                      sx={{ borderRadius: 1, borderColor: "divider" }}
                    >
                      {loading ? <CircularProgress size={20} /> : "Continue"}
                    </Button>
                    <Button
                      size="small"
                      onClick={() => {
                        setShowEmailLogin(false);
                        setEmail("");
                        setError("");
                      }}
                      sx={{ textTransform: "none" }}
                    >
                      Cancel
                    </Button>
                  </>
                )}
              </>
            )}

            {step === "password" && (
              <>
                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  fullWidth
                  disabled
                />
                <TextField
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handlePasswordLogin()}
                  fullWidth
                  autoFocus
                  error={Boolean(error)}
                  helperText={error}
                  disabled={loading}
                />
                <Button
                  variant="contained"
                  onClick={handlePasswordLogin}
                  fullWidth
                  disabled={loading || !password}
                  sx={{ borderRadius: 1 }}
                >
                  {loading ? <CircularProgress size={24} /> : "Login"}
                </Button>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Button size="small" onClick={handleBackToEmail}>
                    Change email
                  </Button>
                  <Button
                    size="small"
                    component={Link}
                    to="/forgot-password"
                    state={{ email }}
                  >
                    Forgot password?
                  </Button>
                </Stack>
              </>
            )}

            {step === "setup-password" && (
              <>
                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  fullWidth
                  disabled
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 1 }}
                >
                  Your account is approved for password authentication. Please
                  set up a password to continue.
                </Typography>
                <Button
                  variant="contained"
                  component={Link}
                  to="/signup"
                  state={{ email }}
                  fullWidth
                  sx={{ borderRadius: 1 }}
                >
                  Set Up Password
                </Button>
                <Button size="small" onClick={handleBackToEmail} fullWidth>
                  Change email
                </Button>
              </>
            )}

            {step === "magic-link" && (
              <>
                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  fullWidth
                  disabled
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 1 }}
                >
                  No password set for this account. You can log in using a magic
                  link sent to your email, or contact your administrator to set
                  up password authentication.
                </Typography>
                <Button
                  variant="contained"
                  component={Link}
                  to="/login/link"
                  state={{ email }}
                  fullWidth
                  sx={{ borderRadius: 1 }}
                >
                  Send login link
                </Button>
                <Button size="small" onClick={handleBackToEmail} fullWidth>
                  Change email
                </Button>
              </>
            )}
          </Stack>
        </Box>
      </Stack>
      <img
        src={logo}
        style={{ position: "fixed", bottom: 15, right: 15, width: 50 }}
      />
    </>
  );
};
